import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { hasCoPowers, type Role } from '@cosvision/shared';
import { useAuthStore } from '../../store/auth-store.js';

interface RoleGateProps {
  allow: readonly Role[];
  children: ReactNode;
  /** Rendered when the user does not have one of the allowed roles. */
  fallback?: ReactNode;
}

/**
 * Hide / replace a subtree based on the current user's role. Use inside
 * ProtectedRoute — assumes `user` is already populated.
 */
export function RoleGate({ allow, children, fallback = null }: RoleGateProps) {
  const user = useAuthStore((s) => s.user);
  if (!user || !allow.includes(user.role)) {
    return <>{fallback}</>;
  }
  return <>{children}</>;
}

function AccessDenied() {
  return (
    <div className="card mx-auto mt-12 max-w-lg text-center">
      <h2 className="text-lg font-semibold">Access denied</h2>
      <p className="mt-2 text-sm text-slate-500">
        Your role does not have permission to view this page.
      </p>
      <Link to="/" className="btn-primary mt-4 inline-block">
        Back to home
      </Link>
    </div>
  );
}

/**
 * Hard route guard — redirects to `/` when the user's role is not allowed.
 * Use to gate whole pages (e.g. Settings, AI Co-Pilot).
 */
export function RequireRole({ allow, children }: RoleGateProps) {
  const user = useAuthStore((s) => s.user);
  if (!user || !allow.includes(user.role)) {
    return <AccessDenied />;
  }
  return <>{children}</>;
}

/**
 * Hard route guard for command-level pages — admits the CO and anyone with
 * the CO's authority (the 2IC). Use for every page that was previously
 * `RequireRole allow={['CO']}`.
 */
export function RequireCoPowers({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!hasCoPowers(user)) {
    return <AccessDenied />;
  }
  return <>{children}</>;
}
